export type ReceiveStatus = 'pending' | 'partial' | 'completed';

export interface InventoryTransferItemAPIInterface {
  id: number;
  inventory_transfer_id: number;
  product_variant_id: number;
  quantity: number;
  note: string | null;
  created_at: string;
  updated_at: string;
  // Product information
  product_id?: number;
  product_title?: string;
  variant_title?: string;
  sku?: string;
  image_url?: string;
  unit_cost?: number | null;
  // Priority scores
  priority_score?: number;
  stockout_risk_score?: number;
  revenue_impact_score?: number;
  lead_time_urgency_score?: number;
  // Receiving tracking
  quantity_received?: number;
  receive_status?: ReceiveStatus;
}

export interface InventoryTransferItemInterface {
  id: number;
  inventoryTransferId: number;
  productVariantId: number;
  quantity: number;
  note: string;
  createdAt: string;
  updatedAt: string;
  // Product information
  productId?: number;
  productTitle?: string;
  variantTitle?: string;
  sku?: string;
  imageUrl?: string;
  unitCost?: number | null;
  // Priority scores
  priorityScore?: number;
  stockoutRiskScore?: number;
  revenueImpactScore?: number;
  leadTimeUrgencyScore?: number;
  // Receiving tracking
  quantityReceived?: number;
  receiveStatus?: ReceiveStatus;
}

export class InventoryTransferItemModel {
  public id: number;
  public inventoryTransferId: number;
  public productVariantId: number;
  public quantity: number;
  public note: string;
  public createdAt: string;
  public updatedAt: string;
  // Product information
  public productId: number;
  public productTitle: string;
  public variantTitle: string;
  public sku: string;
  public imageUrl: string;
  public unitCost: number | null;
  // Priority scores
  public priorityScore: number;
  public stockoutRiskScore: number;
  public revenueImpactScore: number;
  public leadTimeUrgencyScore: number;
  // Receiving tracking
  public quantityReceived: number;
  public receiveStatus: ReceiveStatus;

  constructor(data?: Partial<InventoryTransferItemModel>) {
    this.id = data?.id ?? 0;
    this.inventoryTransferId = data?.inventoryTransferId ?? 0;
    this.productVariantId = data?.productVariantId ?? 0;
    this.quantity = data?.quantity ?? 0;
    this.note = data?.note ?? '';
    this.createdAt = data?.createdAt ?? '';
    this.updatedAt = data?.updatedAt ?? '';
    // Product information
    this.productId = data?.productId ?? 0;
    this.productTitle = data?.productTitle ?? '';
    this.variantTitle = data?.variantTitle ?? '';
    this.sku = data?.sku ?? '';
    this.imageUrl = data?.imageUrl ?? '';
    this.unitCost = data?.unitCost ?? null;
    // Priority scores
    this.priorityScore = data?.priorityScore ?? 0;
    this.stockoutRiskScore = data?.stockoutRiskScore ?? 0;
    this.revenueImpactScore = data?.revenueImpactScore ?? 0;
    this.leadTimeUrgencyScore = data?.leadTimeUrgencyScore ?? 0;
    // Receiving tracking
    this.quantityReceived = data?.quantityReceived ?? 0;
    this.receiveStatus = data?.receiveStatus ?? 'pending';
  }

  public mapData(data: any): this {
    this.id = data.id;
    this.inventoryTransferId = data.inventoryTransferId || data.inventory_transfer_id;
    this.productVariantId = data.productVariantId || data.product_variant_id;
    this.quantity = data.quantity;
    this.note = data.note || '';
    this.createdAt = data.createdAt || data.created_at;
    this.updatedAt = data.updatedAt || data.updated_at;
    // Product information
    this.productId = data.productId || data.product_id || 0;
    this.productTitle = data.productTitle || data.product_title || '';
    this.variantTitle = data.variantTitle || data.variant_title || '';
    this.sku = data.sku || '';
    this.imageUrl = data.imageUrl || data.image_url || '';
    this.unitCost = data.unitCost || data.unit_cost || null;
    // Priority scores
    this.priorityScore = data.priorityScore || data.priority_score || 0;
    this.stockoutRiskScore = data.stockoutRiskScore || data.stockout_risk_score || 0;
    this.revenueImpactScore = data.revenueImpactScore || data.revenue_impact_score || 0;
    this.leadTimeUrgencyScore = data.leadTimeUrgencyScore || data.lead_time_urgency_score || 0;
    // Receiving tracking
    this.quantityReceived = data.quantityReceived || data.quantity_received || 0;
    this.receiveStatus = (data.receiveStatus || data.receive_status || 'pending') as ReceiveStatus;
    return this;
  }

  public get formattedCreatedAt(): string {
    return new Date(this.createdAt).toLocaleDateString();
  }

  public get formattedUpdatedAt(): string {
    return new Date(this.updatedAt).toLocaleDateString();
  }

  public get totalValue(): number {
    return this.unitCost ? this.unitCost * this.quantity : 0;
  }

  public get priorityLevel(): string {
    if (this.priorityScore >= 80) { return 'Critical'; }
    if (this.priorityScore >= 60) { return 'High'; }
    if (this.priorityScore >= 30) { return 'Normal'; }
    return 'Low';
  }

  public get priorityColor(): string {
    switch (this.priorityLevel) {
      case 'Critical': return '#f6687b';
      case 'High': return '#faaf3b';
      case 'Normal': return '#59C8E4';
      case 'Low': return '#99be50';
      default: return '#999';
    }
  }

  public get displayName(): string {
    if (this.variantTitle && this.variantTitle !== 'Default Title') {
      return `${this.productTitle} - ${this.variantTitle}`;
    }
    return this.productTitle;
  }

  // Receiving status helpers
  public get isFullyReceived(): boolean {
    return this.quantityReceived >= this.quantity;
  }

  public get isPartiallyReceived(): boolean {
    return this.quantityReceived > 0 && this.quantityReceived < this.quantity;
  }

  public get isPendingReceive(): boolean {
    return this.quantityReceived === 0;
  }

  public get hasOverage(): boolean {
    return this.quantityReceived > this.quantity;
  }

  public get receiveVariance(): number {
    return this.quantityReceived - this.quantity;
  }

  public get receiveVariancePercentage(): number {
    if (this.quantity > 0) {
      return (this.receiveVariance / this.quantity) * 100;
    }
    return 0;
  }

  public get remainingToReceive(): number {
    return Math.max(0, this.quantity - this.quantityReceived);
  }

  public get receiveStatusColor(): string {
    switch (this.receiveStatus) {
      case 'completed': return 'green';
      case 'partial': return 'amber';
      case 'pending': return 'grey';
      default: return 'grey';
    }
  }

  public get receiveStatusIcon(): string {
    switch (this.receiveStatus) {
      case 'completed': return 'mdi-check-circle';
      case 'partial': return 'mdi-clock-outline';
      case 'pending': return 'mdi-package-variant-closed';
      default: return 'mdi-help-circle';
    }
  }

  public get receiveStatusText(): string {
    switch (this.receiveStatus) {
      case 'completed': return 'Completed';
      case 'partial': return 'Partial';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  }

  public get varianceIndicator(): string {
    if (this.hasOverage) {
      return this.receiveVariancePercentage > 10 ? 'severe-overage' : 'overage';
    }
    if (this.isFullyReceived) {
      return 'match';
    }
    if (this.isPartiallyReceived) {
      return 'shortage';
    }
    return 'pending';
  }

  public get varianceColor(): string {
    switch (this.varianceIndicator) {
      case 'match': return 'green';
      case 'shortage': return 'blue';
      case 'overage': return 'yellow';
      case 'severe-overage': return 'red';
      case 'pending': return 'grey';
      default: return 'grey';
    }
  }
}
